// ────────────────────────────────────────────────────────────────
// Checklist personnalisée — dashboard
// Générée à partir des priorités choisies au questionnaire d'onboarding
// (ActivityQuestionnaire.jsx). Chaque étape est cochée en fonction de
// l'état réel des données (server/routes/onboarding.js GET /checklist/:cid),
// jamais une case cochée manuellement déconnectée de la réalité.
// Invisible si la société n'a pas répondu au questionnaire (sociétés
// créées avant ce changement) ou si toutes les étapes sont déjà faites.
// ────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { CheckCircle, Circle, ChevronDown, ChevronUp, ListChecks } from "lucide-react";
import api from "../services/api";

function storageKey(companyId) { return `onboarding_checklist_collapsed_${companyId}`; }

export default function OnboardingChecklist({ currentCompanyId, onNavigate }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    try {
      setCollapsed(localStorage.getItem(storageKey(currentCompanyId)) === "true");
    } catch { setCollapsed(false); }
    api.onboarding.checklist(currentCompanyId)
      .then(setData)
      .catch(() => setData({ enabled: false, items: [] }))
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(storageKey(currentCompanyId), String(next)); } catch {}
      return next;
    });
  };

  if (loading || !data?.enabled) return null;

  const { items } = data;
  const doneCount = items.filter(i => i.done).length;
  const allDone   = doneCount === items.length;
  if (allDone) return null; // "tant que non complétée" — plus rien à afficher une fois tout fait

  return (
    <div style={{
      background: "var(--bg-card)", border: "var(--border)", borderRadius: 12,
      padding: collapsed ? "12px 18px" : "16px 18px", marginBottom: 16,
    }}>
      <div
        onClick={toggleCollapsed}
        style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
      >
        <ListChecks size={16} color="var(--accent)" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
          Bien démarrer avec ExecPro360
          <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 8 }}>
            {doneCount}/{items.length}
          </span>
        </div>
        {collapsed ? <ChevronDown size={15} color="var(--text-muted)" /> : <ChevronUp size={15} color="var(--text-muted)" />}
      </div>

      {!collapsed && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map(item => (
            <div
              key={item.key}
              onClick={() => !item.done && onNavigate?.(item.page)}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                borderRadius: 8, cursor: item.done ? "default" : "pointer",
                background: item.done ? "transparent" : "rgba(37,99,235,0.05)",
              }}
            >
              {item.done
                ? <CheckCircle size={16} color="var(--success)" style={{ flexShrink: 0 }} />
                : <Circle size={16} color="var(--text-muted)" style={{ flexShrink: 0 }} />}
              <span style={{
                fontSize: 12.5, color: item.done ? "var(--text-muted)" : "var(--text-primary)",
                textDecoration: item.done ? "line-through" : "none",
              }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
